// src/pages/Dashboard/TempleStatisticsSummary.jsx
// สรุปสถิติประจำปีจากแบบฟอร์ม (ตาราง templeStatistics) แสดงเป็นตาราง + กราฟ
import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, Row, Statistic, Table, Radio, Tag, message, Spin, Empty, Typography } from 'antd';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import axios from 'axios';
import API_BASE_URL from '../../config/api';

const { Text } = Typography;

const fmt = (n) => Number(n || 0).toLocaleString('th-TH');

// ฟิลด์ตัวเลขที่รวมสรุป (ตรงกับที่ Backend คืนมา)
const METRIC_KEYS = [
  'monks_count', 'novices_count', 'temple_students_count', 'pariyatti_count',
  'monks_naktham3_count', 'novices_naktham3_count', 'monks_patimokkha_count',
  'monks_over10_pansa_count', 'monks_with_titles_count', 'total_former_abbots',
  'priyatti_students_count', 'land_temple_rai', 'land_dharma_rai',
];

const zeroAgg = () => ({ reporting_temples: 0, ...Object.fromEntries(METRIC_KEYS.map((k) => [k, 0])) });

function sumAgg(rows) {
  const agg = zeroAgg();
  for (const r of rows || []) {
    agg.reporting_temples += r.reporting_temples || 0;
    for (const k of METRIC_KEYS) agg[k] += r[k] || 0;
  }
  return agg;
}

const numCol = (title, key) => ({
  title,
  dataIndex: key,
  key,
  align: 'right',
  render: (v) => fmt(v),
});

const naktham3Col = {
  title: 'นักธรรมเอก',
  dataIndex: 'naktham3',
  key: 'naktham3',
  align: 'right',
  render: (_, r) => fmt((r.monks_naktham3_count || 0) + (r.novices_naktham3_count || 0)),
};

const TempleStatisticsSummary = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null); // null = ทุกปี (รวม)

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        // หน้า Dashboard แสดงภาพรวมทั้งภาคให้ทุก Level (เฉพาะหน้านี้)
        const sumRes = await axios.get(`${API_BASE_URL}/api/temple-statistics/summary`, {
          headers,
          params: { all: 1 },
        });
        setData(sumRes.data);
        setSelectedYear(sumRes.data.latestYear ?? null);
      } catch (err) {
        message.error('ไม่สามารถโหลดสรุปสถิติประจำปีได้');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ตัวเลขสำหรับการ์ด (ปีที่เลือก หรือรวมทุกปี)
  const activeAgg = useMemo(() => {
    if (!data || !data.byYear || !data.byYear.length) return zeroAgg();
    if (selectedYear === null) return sumAgg(data.byYear);
    return data.byYear.find((y) => y.year === selectedYear) || zeroAgg();
  }, [data, selectedYear]);

  // แถวสรุปแยกจังหวัด (ปีที่เลือก หรือรวมทุกปี) - กรองกลุ่ม "(ไม่ระบุจังหวัด)" ออก
  const provinceRows = useMemo(() => {
    if (!data || !data.byProvinceByYear || !data.byProvinceByYear.length) return [];
    const hasProvince = (r) => r.province && r.province !== '(ไม่ระบุจังหวัด)';
    if (selectedYear !== null) {
      return data.byProvinceByYear.filter((r) => r.year === selectedYear && hasProvince(r));
    }
    const map = new Map();
    for (const r of data.byProvinceByYear) {
      if (!hasProvince(r)) continue;
      if (!map.has(r.province)) map.set(r.province, { province: r.province, ...zeroAgg() });
      const agg = map.get(r.province);
      agg.reporting_temples += r.reporting_temples || 0;
      for (const k of METRIC_KEYS) agg[k] += r[k] || 0;
    }
    return [...map.values()];
  }, [data, selectedYear]);

  // ข้อมูลกราฟ
  const trendData = useMemo(
    () => (data?.byYear || []).map((r) => ({
      label: `${r.year + 543}`,
      ภิกษุ: r.monks_count || 0,
      สามเณร: r.novices_count || 0,
      ศิษย์วัด: r.temple_students_count || 0,
      เปรียญ: r.pariyatti_count || 0,
    })),
    [data]
  );

  const provinceBarData = useMemo(
    () => provinceRows.map((r) => ({ จังหวัด: r.province, ภิกษุ: r.monks_count || 0, สามเณร: r.novices_count || 0 })),
    [provinceRows]
  );

  // วัดที่ส่งข้อมูล vs วัดทั้งหมด (ต่อจังหวัด)
  const reportingData = useMemo(() => {
    return (data?.templeCountByProvince || []).map((p) => {
      const rep = provinceRows.find((pr) => pr.province === p.province);
      return { จังหวัด: p.province, ทั้งหมด: p.total || 0, 'ส่งข้อมูล': rep ? rep.reporting_temples : 0 };
    });
  }, [data, provinceRows]);

  const yearColumns = [
    { title: 'ปี (พ.ศ.)', dataIndex: 'year', key: 'year', render: (v) => `${v + 543}` },
    numCol('วัดที่ส่งข้อมูล', 'reporting_temples'),
    numCol('ภิกษุ', 'monks_count'),
    numCol('สามเณร', 'novices_count'),
    numCol('ศิษย์วัด', 'temple_students_count'),
    numCol('เปรียญ', 'pariyatti_count'),
    naktham3Col,
    numCol('ปาฏิโมกข์', 'monks_patimokkha_count'),
    numCol('พรรษา 10+', 'monks_over10_pansa_count'),
    numCol('สมณศักดิ์', 'monks_with_titles_count'),
    numCol('อดีตเจ้าอาวาส', 'total_former_abbots'),
    numCol('นักเรียนปริยัติ', 'priyatti_students_count'),
    numCol('ที่ดินวัด (ไร่)', 'land_temple_rai'),
    numCol('ที่ธรณีสงฆ์ (ไร่)', 'land_dharma_rai'),
  ];

  const provinceColumns = [
    { title: 'จังหวัด', dataIndex: 'province', key: 'province', fixed: 'left' },
    numCol('วัดที่ส่งข้อมูล', 'reporting_temples'),
    numCol('ภิกษุ', 'monks_count'),
    numCol('สามเณร', 'novices_count'),
    numCol('ศิษย์วัด', 'temple_students_count'),
    numCol('เปรียญ', 'pariyatti_count'),
    naktham3Col,
    numCol('ปาฏิโมกข์', 'monks_patimokkha_count'),
    numCol('พรรษา 10+', 'monks_over10_pansa_count'),
    numCol('สมณศักดิ์', 'monks_with_titles_count'),
    numCol('นักเรียนปริยัติ', 'priyatti_students_count'),
    numCol('ที่ดินวัด (ไร่)', 'land_temple_rai'),
  ];

  // ข้อมูลบนหน้า Dashboard เป็นภาพรวมทั้งภาค (ทุก Level เห็นเหมือนกัน)
  const scopeLabel = 'ทั่วทั้งภาค ๔-๕ (ธ)';

  const cards = [
    { title: 'วัดที่ส่งข้อมูล', value: activeAgg.reporting_temples, color: '#1890ff' },
    { title: 'พระภิกษุ', value: activeAgg.monks_count, color: '#ad7c00' },
    { title: 'สามเณร', value: activeAgg.novices_count, color: '#52c41a' },
    { title: 'ศิษย์วัด', value: activeAgg.temple_students_count, color: '#fa8c16' },
    { title: 'ภิกษุ-สามเณรเปรียญ', value: activeAgg.pariyatti_count, color: '#722ed1' },
    { title: 'นักเรียนปริยัติ', value: activeAgg.priyatti_students_count, color: '#13c2c2' },
  ];

  return (
    <Spin spinning={loading}>
      <Card title="สรุปสถิติประจำปีจากแบบฟอร์ม" style={{ marginTop: 24 }} extra={<Tag color="gold">{scopeLabel}</Tag>}>
        {!data || !data.byYear || data.byYear.length === 0 ? (
          <Empty description="ยังไม่มีข้อมูลสถิติประจำปี (templeStatistics)" />
        ) : (
          <>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <Radio.Group
                value={selectedYear === null ? 'all' : selectedYear}
                onChange={(e) => setSelectedYear(e.target.value === 'all' ? null : Number(e.target.value))}
              >
                <Radio.Button value="all">ทุกปี (รวม)</Radio.Button>
                {(data.years || []).map((y) => (
                  <Radio.Button key={y} value={y}>{y + 543}</Radio.Button>
                ))}
              </Radio.Group>
              <Text type="secondary">
                {selectedYear === null
                  ? `รวมข้อมูลปี พ.ศ. ${Math.min(...(data.years || [0])) + 543} - ${Math.max(...(data.years || [0])) + 543}`
                  : `ข้อมูลปี พ.ศ. ${selectedYear + 543}`}
              </Text>
            </div>

            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              {cards.map((c) => (
                <Col xs={12} sm={8} md={4} key={c.title}>
                  <Card size="small" style={{ borderTop: `3px solid ${c.color}` }}>
                    <Statistic title={c.title} value={c.value} valueStyle={{ fontSize: 22 }} />
                  </Card>
                </Col>
              ))}
            </Row>

            <Card size="small" title="แนวโน้มรายปี" style={{ marginBottom: 24 }}>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendData} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="ภิกษุ" stroke="#ad7c00" strokeWidth={2} />
                  <Line type="monotone" dataKey="สามเณร" stroke="#52c41a" strokeWidth={2} />
                  <Line type="monotone" dataKey="ศิษย์วัด" stroke="#1890ff" strokeWidth={2} />
                  <Line type="monotone" dataKey="เปรียญ" stroke="#722ed1" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Card>

            <Card size="small" title="ตารางสรุปรายปี" style={{ marginBottom: 24 }}>
              <Table
                columns={yearColumns}
                dataSource={data.byYear}
                rowKey="year"
                size="small"
                bordered
                pagination={false}
                scroll={{ x: 'max-content' }}
                summary={() => {
                  const t = sumAgg(data.byYear);
                  return (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}><strong>รวม</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={1}><strong>{fmt(t.reporting_temples)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={2}><strong>{fmt(t.monks_count)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={3}><strong>{fmt(t.novices_count)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={4}><strong>{fmt(t.temple_students_count)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={5}><strong>{fmt(t.pariyatti_count)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={6}><strong>{fmt(t.monks_naktham3_count + t.novices_naktham3_count)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={7}><strong>{fmt(t.monks_patimokkha_count)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={8}><strong>{fmt(t.monks_over10_pansa_count)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={9}><strong>{fmt(t.monks_with_titles_count)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={10}><strong>{fmt(t.total_former_abbots)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={11}><strong>{fmt(t.priyatti_students_count)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={12}><strong>{fmt(t.land_temple_rai)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={13}><strong>{fmt(t.land_dharma_rai)}</strong></Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            </Card>

            <Card size="small" title={`ตารางสรุปแยกจังหวัด (${selectedYear === null ? 'รวมทุกปี' : 'พ.ศ. ' + (selectedYear + 543)})`} style={{ marginBottom: 24 }}>
              {provinceRows.length === 0 ? (
                <Empty description="ไม่มีข้อมูลในปีที่เลือก" />
              ) : (
                <Table
                  columns={provinceColumns}
                  dataSource={provinceRows}
                  rowKey="province"
                  size="small"
                  bordered
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                  summary={() => {
                    const t = sumAgg(provinceRows);
                    return (
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0}><strong>รวม</strong></Table.Summary.Cell>
                        <Table.Summary.Cell index={1}><strong>{fmt(t.reporting_temples)}</strong></Table.Summary.Cell>
                        <Table.Summary.Cell index={2}><strong>{fmt(t.monks_count)}</strong></Table.Summary.Cell>
                        <Table.Summary.Cell index={3}><strong>{fmt(t.novices_count)}</strong></Table.Summary.Cell>
                        <Table.Summary.Cell index={4}><strong>{fmt(t.temple_students_count)}</strong></Table.Summary.Cell>
                        <Table.Summary.Cell index={5}><strong>{fmt(t.pariyatti_count)}</strong></Table.Summary.Cell>
                        <Table.Summary.Cell index={6}><strong>{fmt(t.monks_naktham3_count + t.novices_naktham3_count)}</strong></Table.Summary.Cell>
                        <Table.Summary.Cell index={7}><strong>{fmt(t.monks_patimokkha_count)}</strong></Table.Summary.Cell>
                        <Table.Summary.Cell index={8}><strong>{fmt(t.monks_over10_pansa_count)}</strong></Table.Summary.Cell>
                        <Table.Summary.Cell index={9}><strong>{fmt(t.monks_with_titles_count)}</strong></Table.Summary.Cell>
                        <Table.Summary.Cell index={10}><strong>{fmt(t.priyatti_students_count)}</strong></Table.Summary.Cell>
                        <Table.Summary.Cell index={11}><strong>{fmt(t.land_temple_rai)}</strong></Table.Summary.Cell>
                      </Table.Summary.Row>
                    );
                  }}
                />
              )}
            </Card>


            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <Card size="small" title={`พระภิกษุ/สามเณรแยกจังหวัด (${selectedYear === null ? 'รวมทุกปี' : 'พ.ศ. ' + (selectedYear + 543)})`}>
                  <ResponsiveContainer width="100%" height={340}>
                    <BarChart data={provinceBarData} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="จังหวัด" angle={-45} textAnchor="end" interval={0} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="ภิกษุ" fill="#ad7c00" />
                      <Bar dataKey="สามเณร" fill="#52c41a" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card size="small" title={`วัดที่ส่งข้อมูลเทียบกับวัดทั้งหมด (รายจังหวัด, ${selectedYear === null ? 'รวมทุกปี' : 'พ.ศ. ' + (selectedYear + 543)})`}>
                  <ResponsiveContainer width="100%" height={340}>
                    <BarChart data={reportingData} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="จังหวัด" angle={-45} textAnchor="end" interval={0} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="ทั้งหมด" fill="#bfbfbf" />
                      <Bar dataKey="ส่งข้อมูล" fill="#1890ff" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Card>
    </Spin>
  );
};

export default TempleStatisticsSummary;

